/**
 * Full content from PDF - Configuration (2.2), Coordinate Systems (2.3), DXF (2.4), Simple GUI (2.5)
 */
const C = (title, html) => ({ title, html });

export const content2 = {
  "2-2-1-settings": C(
    "2.2.1 Settings in x_translator_settings.magik",
    `<p>Shared constants and methods defined in the file x_translator_settings.magik can be configured. To change such a value, it should not be changed directly (inside the x_translator_engine module), but should be copied to x_translator_client_settings.magik and changed there.</p>
<p>The shared constants correspond usually to the default values that are used in the options. The available parameters are documented in the user and reference manuals and in the source code comments.</p>`
  ),

  "2-2-2-formats": C(
    "2.2.2 List of Formats",
    `<p>In some configurations you need the internal name of the SEPM X-Translator format. Use the following list to determine the internal name:</p>
<table><thead><tr><th>Internal Name</th><th>External Name (shown in GUI)</th></tr></thead>
<tbody>
<tr><td>smallworld</td><td>Smallworld</td></tr>
<tr><td>mif</td><td>MIF</td></tr>
<tr><td>shape</td><td>Shape</td></tr>
<tr><td>interlis1</td><td>INTERLIS 1</td></tr>
<tr><td>interlis2</td><td>INTERLIS 2</td></tr>
<tr><td>tfd</td><td>TFM/TFD</td></tr>
<tr><td>acad</td><td>AutoCAD DWG/DXF</td></tr>
<tr><td>dxf</td><td>DXF</td></tr>
<tr><td>text</td><td>Text</td></tr>
<tr><td>postgis</td><td>PostGIS</td></tr>
<tr><td>kml</td><td>KML</td></tr>
<tr><td>geojson</td><td>GeoJSON</td></tr>
<tr><td>gdal</td><td>GDAL/OGR</td></tr>
<tr><td>isybau</td><td>ISYBAU</td></tr>
<tr><td>oracle</td><td>Oracle</td></tr>
</tbody></table>`
  ),

  "2-2-3-user-formats": C(
    "2.2.3 Configuration of User-Visible Formats",
    `<p>System integrators often need to restrict the formats visible in the user interface to reduce complexity and improve usability. The following configurations can be used to achieve this:</p>
<table><thead><tr><th>Method on x_translator_settings</th><th>Description</th></tr></thead>
<tbody>
<tr><td>unregister_available_source_format( p_format )</td><td>Hides a specific source format from the user interface.</td></tr>
<tr><td>unregister_available_target_format( p_format )</td><td>Hides a specific target format from the user interface.</td></tr>
<tr><td>unregister_all_available_source_formats()</td><td>Clears the list of all source formats displayed to the user.</td></tr>
<tr><td>unregister_all_available_target_formats()</td><td>Clears the list of all target formats displayed to the user.</td></tr>
<tr><td>register_available_source_format( p_format, p_name )</td><td>Makes a specific source format visible to the user, with a custom name if desired.</td></tr>
<tr><td>register_available_target_format( p_format, p_name )</td><td>Makes a specific target format visible to the user, with a custom name if desired.</td></tr>
</tbody></table>
<p>Example 1: Removing Specific Formats: To hide only the "INTERLIS 1" format:</p>
<pre>_block
x_translator_settings.unregister_available_source_format(:interlis1)
x_translator_settings.unregister_available_target_format(:interlis1)
_endblock
$</pre>
<p>Example 2: Restricting to Specific Formats: If you want to display only "Smallworld" and "AutoCAD DWG/DXF" formats:</p>
<pre>_block
x_translator_settings.unregister_all_available_source_formats()
x_translator_settings.unregister_all_available_target_formats()
x_translator_settings.register_available_source_format(:smallworld, "Smallworld")
x_translator_settings.register_available_target_format(:smallworld, "Smallworld")
x_translator_settings.register_available_source_format(:acad, "AutoCAD DWG/DXF")
x_translator_settings.register_available_target_format(:acad, "AutoCAD DWG/DXF")
_endblock
$</pre>
<p>The effect of Example 2 will be, that only the Smallworld and AutoCAD formats are available in the user interface and that the Options GUI only shows the corresponding formats: The selection of formats has been limited to Smallworld and AutoCAD. Options only contain the tabs General, Smallworld Source/Target, AutoCAD and Transform</p>`
  ),

  "2-2-4-gui-defaults": C(
    "2.2.4 GUI Defaults",
    `<p>It is often a requirement to set the default values of the widgets in the user interface. This is possible by using the following configuration features:</p>
<table><thead><tr><th>x_translator_settings</th><th>Description</th></tr></thead>
<tbody>
<tr><td>default_source_format</td><td>The source format selected when the user interface is opened the first time.</td></tr>
<tr><td>default_target_format</td><td>The target format selected when the user interface is opened the first time.</td></tr>
<tr><td>get_default_source_dir()<br>get_default_source_path( p_format )</td><td>Default directories and files shown in the dialog of the source specification.</td></tr>
<tr><td>get_default_target_dir()<br>get_default_target_path( p_format )</td><td>Default directories and files shown in the dialog of the target specification.</td></tr>
<tr><td>default_menu_properties</td><td>Configuration of arbitrary items in the source and target specification dialogs. For example this is the place to define default coordinate and unit systems for exports to DXF/DWG/Shape/MIF etc.</td></tr>
<tr><td>x_translator_dir</td><td>Default directory for log files.</td></tr>
<tr><td>default_save_load_directory</td><td>Default directory for saving and loading mapping files.</td></tr>
</tbody></table>`
  ),

  "2-2-5-default-values": C(
    "2.2.5 Default Values in Source and Target Dialogs",
    `<p>This section explains how to change the default value of a setting in one of the source or target format dialogs.</p>
<p>Example: Preselect "Layer=Collection" for the AutoCAD source "Model Mode" parameter when the dialog opens.</p>
<ul>
<li><strong>Identify the Internal Name of the dialog</strong><br>Check the source code (path: x_translator\\modules\\translator\\x_translator_swift\\source\\menus\\acad\\x_translator_acad_source_spec_menu.magik) to find the internal name of the dialog. The internal name here is: x_translator_acad_source_spec_menu</li>
<li><strong>Identify the Internal Name of the GUI widget</strong><br>Check the source code to find the internal name of the GUI widget. Example from the source code: .gui_items[:ci_acad_model_mode] &lt;&lt; text_choice_item.new(...) The internal name here is: ci_acad_model_mode</li>
<li><strong>Identify the Internal Name of the GUI widget's new default value</strong><br>The method x_translator_acad_source_spec_menu.acad_model_mode_list shows the possible values, in this example the value should be put to layers</li>
<li><strong>Configure x_translator_settings.default_menu_properties</strong><br>Now you can configure the new default value like this: x_translator_settings.default_menu_properties[:x_translator_acad_source_spec_menu][:ci_acad_model_mode] &lt;&lt; :layers</li>
</ul>`
  ),

  "2-2-6-x-base-settings": C(
    "2.2.6 Settings in x_base_settings.magik",
    `<table><thead><tr><th>Parameter</th><th>Description</th></tr></thead>
<tbody>
<tr><td>is_pretty_print</td><td>When set to true, generated XML files contain extra spaces for improved readability in a text editor.</td></tr>
</tbody></table>`
  ),

  "2-2-7-options-menu": C(
    "2.2.7 Default values in the options menu",
    `<p>This section explains how to change the default value of a setting in the X-Translator Options Dialog. Any settings defined in the x_translator_client_settings.magik file will override the default values in x_translator_settings.magik. This allows for flexible customization to meet customer requirements.</p>
<p>Example: Changing the Default Value for "Represent import and export area"</p>
<ul>
<li><strong>Identify the Internal Name</strong><br>Use either the user manual or check the source code (path: x_translator\\modules\\translator\\x_translator_swift\\source\\plugins\\x_translator_options_gui.magik) to find the internal name of the setting. Example from the user manual: Option "Represent import and export area" - internal name is_represent_transfer_area. If activated, the import or export area is displayed as a blue rectangle or polygon.</li>
<li><strong>Identify the Shared Constant</strong><br>Open the x_translator_settings.magik file and locate the corresponding shared constant: :is_represent_transfer_area</li>
<li><strong>Adjust the Default Value</strong><br>Copy the shared constant to your customer module file, e.g., x_translator_client_settings.magik, and modify the value as needed. Example of changing the default value from True to False: set _false instead of _true</li>
<li><strong>Verify Changes</strong><br>At the next startup of the X-Translator in a new session, the updated default value will be displayed in the options dialog.</li>
</ul>`
  ),

  "2-2-8-override-path": C(
    "2.2.8 Override default Path directory shown in dialogs",
    `<p><strong>Goal.</strong> Control the initial directory proposed by file/path choosers (e.g. for DWG/DXF).</p>
<p><strong>Where.</strong> Implement as a customer override in the x_translator_client_settings.magik file.</p>
<pre>_pragma(classify_level=advanced,topic={x_translator},usage=redefinable)
_method x_translator_settings.get_default_source_dir()
## Parameters :
## Returns :
## Function :
_return system.getenv("USERPROFILE")
_endmethod
$</pre>`
  ),

  "2-2-9-authorisation": C(
    "2.2.9 Authorisation",
    `<p>The following options are available for configuring access to SEPM X-Translators. The configurations are defined on the class x_translator_settings:</p>
<ul>
<li><strong>gui_auth_group</strong> - Only users in the given authorisation group X-Translator or system administrators can open the user interface. This configuration is active in the standard distribution, but it can be switched off if desired.</li>
<li><strong>sepm_export_auth_group_config, sepm_import_auth_group_config</strong> - Detailed configuration of formats and databases (e.g. users of the group DXF-Import may only import DXF/DWG files into the database Translator). For this configuration please consult the source code comments. This authorisation is not active by default.</li>
<li><strong>skip_source_datasets, skip_target_datasets</strong> - Can be used to completely skip the given databases on import or export. The standard distribution covers the PNI Template for skip_source_datasets.</li>
<li><strong>simple_gui_auth_group</strong> - Allows individual authorization of the SEPM Simple GUIs</li>
</ul>
<p>Set gui_auth_group to _unset to disable this authorisation option. When active, users must be added to a Smallworld authorisation group X-Translator.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/X-Translator%20Group%20Properties.png" alt="X-Translator Group Properties - adding users to the X-Translator authorisation group" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>X-Translator Group Properties - add users to the authorisation group</figcaption>
</figure>
<p>If the user is not member of the X-Translator group, the action to open the X-Translator user interface is disabled.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/User-Not-Member-X-Translator-Group-disbaled.png" alt="SEPM X-Translator menu option disabled when user is not in X-Translator group" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>X-Translator action disabled when user is not in the authorisation group</figcaption>
</figure>
<p>With configuration entries in x_translator_settings.simple_gui_auth_group you can also authorize the SEPM Simple GUI. The following values and configurations are required: simple_gui: Key in x_translator_settings.simple_gui_config; "X-Translator Simple": Name of the corresponding authorization group. User authorized only to use the SEPM Simple GUI.</p>`
  ),

  "2-3-1-prj-files": C(
    "2.3.1 Creation of prj-files (Shape Export)",
    `<p>To create prj files when exporting to Shape, an entry in the configuration x_translator_settings.shape_prj must be added for each target coordinate system and unit.</p>
<p>In the standard configuration file x_translator_settings_shape_prj.magik in the directory x_translator_engine\\source\\settings\\ some common coordinate systems have been configured.</p>
<p>You can add new entries to your file x_translator_client_settings.magik, respecting the following format:</p>
<pre>x_translator_settings.shape_prj[:|&lt;internal coordinate system name&gt;|] &lt;&lt;
property_list.new_with(
:|&lt;Einheit&gt;|, :|&lt;contents of the prj file&gt;|
)</pre>
<p>Property: Internal coordinate system name - as stored in sw_gis!coordinate_system. Unit - Target unit name. Contents of the prj file - Details of the coordinate system. You can find the required information on http://spatialreference.org. You can also send your entries to support@sepm.ch.</p>`
  ),

  "2-3-2-mif-export": C(
    "2.3.2 CoordSys-Information (MIF-Export)",
    `<p>When exporting to MIF a lookup table from the Smallworld coordinate system to a suitable CoordSys-Information is required.</p>
<p>The standard file x_translator_settings_mif_csys.magik in the directory x_translator_engine\\source\\settings\\ already contains some common configurations.</p>
<p>You can add new entries to your file x_translator_client_settings.magik, respecting the following format:</p>
<pre>x_translator_settings.earth_mif_csys [:|&lt;internal coordinate system name&gt;|] &lt;&lt;
property_list.new_with(
:|&lt;Einheit&gt;|, :|&lt;CoordSys Angabe&gt;|
)</pre>
<p>To find this information the easiest way is to make a test export from MapInfo to a MIF file and to copy the value from there. You can also send your entries to support@sepm.ch.</p>`
  ),

  "2-3-3-inconsistent": C(
    "2.3.3 Message \"Inconsistent Coordinate System\"",
    `<p>If you're getting the message "Inconsistent coordinate system" when configuring the Smallworld source or target formats, then the reason is that there exist coordinate systems with the same name, but different projection parameters. The SEPM X-Translator does not know in this case, which coordinate system should be used.</p>
<p>The script system.check_inconsistent_csys1() (directory x_translator\\scripts\\database) allows listing the relevant coordinate systems. Please delete the defective systems or turn to your GIS administrator in case of doubt.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/Inconsistent%20coordinate%20system%20error.png" alt="Inconsistent coordinate system error dialog in Smallworld Target Specification" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>Inconsistent coordinate system error message</figcaption>
</figure>`
  ),

  "2-4-1-dxf-api": C(
    "2.4.1 DXF Configuration API",
    `<p>To customize DXF layers and other related lists shown in the model properties window, use the following API calls in your Magik configuration:</p>
<table><thead><tr><th>API Call</th><th>Description</th></tr></thead>
<tbody>
<tr><td>x_translator_settings.unregister_dxf_layers()</td><td>Remove all previously registered DXF layers</td></tr>
<tr><td>x_translator_settings.register_dxf_layer( p_dxf_layer )</td><td>Add a new DXF layer. P_DXF_LAYER is a simple vector with: 1. The layer name (string), 2. ACI (AutoCAD Color Index) (integer), 3. Layer linetype (string)</td></tr>
<tr><td>x_translator_settings.register_dxf_line_pattern( p_dxf_line_pattern )</td><td>Add a new DXF line type.</td></tr>
<tr><td>x_translator_settings.register_dxf_line_width( p_dxf_line_width )</td><td>Adds a new DXF line width.</td></tr>
<tr><td>x_translator_settings.register_dxf_text_style( p_dxf_text_style )</td><td>Adds a new DXF text style.</td></tr>
<tr><td>x_translator_settings.dxf_style_mapping</td><td>Mapping of the Smallworld font name to a SEPM DXF text style</td></tr>
</tbody></table>`
  ),

  "2-4-2-dxf-examples": C(
    "2.4.2 DXF Configuration API Examples",
    `<p>Adding Custom DXF Layers:</p>
<pre>x_translator_settings.unregister_dxf_layers()
x_translator_settings.register_dxf_layer( { "GAS LEITUNG IN BETRIEB", 1, "CONTINUOUS" } )
x_translator_settings.register_dxf_layer( { "GAS LEITUNG AUSSER BETRIEB", 1, "CONTINUOUS" } )</pre>
<p>Registering New DXF Line Types:</p>
<pre>x_translator_settings.register_dxf_line_pattern(
property_list.new_with(
:name, :|DASHED|,
:external_name, "Dashed __ __ __ __ __",
:pattern_type, :target_unit,
:pattern, "1.2_0.6" ) )</pre>
<p>Registering DXF Line Widths: x_translator_settings.register_dxf_line_width( {2} )</p>
<p>Registering DXF Text Styles and Mapping Smallworld Fonts to DXF Text Styles (e.g. Smallworld "bold" font maps to "ARIALBD"): x_translator_settings.dxf_style_mapping["bold"] &lt;&lt; "ARIALBD"</p>`
  ),

  "2-4-3-style-mapping": C(
    "2.4.3 Message \"No entry in x_translator_settings.dxf_style_mapping for: xxx\"",
    `<p>If a message like this appears in the console, then the associated DXF style configuration is missing for this Smallworld font name. Example: No entry in x_translator_settings.dxf_style_mapping for: segoe</p>
<p>This can occur with customer-specific font configurations. In this case the configuration for Arial is used. Find the font name (e.g. "Segoe UI" as shown below), register with register_dxf_text_style(), then add x_translator_settings.dxf_style_mapping["segoe"] &lt;&lt; "SEGOE". Please note that the keys in x_translator_settings.dxf_style_mapping must be lowercase.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/Segoe%20UI%20font%20name.png" alt="Segoe UI font - Schriftname (font name) for DXF style mapping configuration" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>Segoe UI font name - use this to configure dxf_style_mapping for customer fonts</figcaption>
</figure>`
  ),

  "2-5-1-registration": C(
    "2.5.1 Registration of a Simple GUI Configuration",
    `<p>The following method is used to register a Simple GUI configuration: x_translator_settings.register_simple_gui_configuration()</p>
<p>The Simple GUI option is available from the application menu:</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/SEPM-Simple-GUI-Option.png" alt="SEPM Simple GUI option in the application menu" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>SEPM Simple GUI option in the menu</figcaption>
</figure>
<p>The characteristics of a Simple GUI configurations are provided as a property_list, which can contain: :external_name (Name displayed in the GUI), :method_name (Executed method), :default_values, :file_or_dir, :path_filter, :path_title (Configuration of the file or directory selection), :right (The name of a right as defined in the Smallworld authorization system. The configuration will only be shown, if the user belongs to a corresponding group with that right.)</p>
<p>Example of a Simple GUI configuration with external_name "Import GeoJSON", method_name, default_values, file_or_dir, path_filter, path_title, and right.</p>`
  ),

  "2-5-2-gui-items": C(
    "2.5.2 Available User-Interface Items (gui_items)",
    `<table><thead><tr><th>gui_items</th><th>Description</th></tr></thead>
<tbody>
<tr><td>:simple_gui_config</td><td>Selection of the Simple-GUI Configuration.</td></tr>
<tr><td>:simple_gui_sub_config</td><td>Selection of the Simple GUI Sub-Configuration. The Sub-Configuration depends on the Configuration. An example is to use the Sub-Configuration to select the export area a) from the trail b) from a configured hard-coded area.</td></tr>
<tr><td>:is_clip_to_trail</td><td>Clip geometries at the trail boundaries (yes/no).</td></tr>
<tr><td>:path</td><td>Path (directory or file depending on the format) for the selection of the source or target data.</td></tr>
<tr><td>:config_path</td><td>Path to configuration file.</td></tr>
<tr><td>:sender</td><td>INTERLIS2 Sender.</td></tr>
<tr><td>:comment</td><td>INTERLIS2 Comment.</td></tr>
<tr><td>:bid</td><td>INTERLIS2 BID.</td></tr>
<tr><td>:oid_prefix</td><td>INTERLIS2 OID Prefix.</td></tr>
<tr><td>:is_tfd_debug_mode</td><td>Debug Mode (yes/no).</td></tr>
<tr><td>:special_transform</td><td>Special transform to use.</td></tr>
</tbody></table>`
  ),

  "2-5-3-properties": C(
    "2.5.3 Properties of the Simple GUI Method",
    `<p>The Simple-GUI-Method receives the following parameters:</p>
<table><thead><tr><th>Properties</th><th>Description</th></tr></thead>
<tbody>
<tr><td>:is_clip_to_trail</td><td>As selected in the GUI.</td></tr>
<tr><td>:path</td><td>As selected in the GUI.</td></tr>
<tr><td>:export_area</td><td>Export area created from the trail.</td></tr>
<tr><td>:ace_control, :gs_factory</td><td>Properties of the current map. For using the 'current visible geometries' the X-Translator engine must be initialised with the given gs_factory object.</td></tr>
<tr><td>:selected_geoms</td><td>Selected geometries in the GIS.</td></tr>
<tr><td>:source_world</td><td>World of the current map (as a x_translator_world object).</td></tr>
</tbody></table>`
  ),

  "2-6-1-overview": C(
    "2.6.1 Overview",
    `<p>The SEPM X-Translator Add-On "Coordinate conversion from LV03/CH1903 to LV95/CH1903+ and vice versa" is based on the SEPM Reframe-ACP for coordinate conversion in Switzerland. If this Add-On is licensed, data can be converted during import or export to and from LV03 or LV95, respectively.</p>`
  ),

  "2-6-2-installation": C(
    "2.6.2 Installation",
    `<p>Please follow the following steps for installation:</p>
<ul>
<li>Execute the setup.exe from Swisstopo on each client</li>
<li>Add the following lines to your environment.bat file:<br>set SEPM_REFRAME_DIR=&lt;Path to SEPM&gt;\\x_translator\\modules\\base\\reframe_acp\\bin<br>set SW_ACP_PATH=%SMALLWORLD_GIS%\\etc\\%PROCESSOR_ARCHITECTURE%;%SEPM_REFRAME_DIR%</li>
<li>Test the conversion with the following calls (this code should not produce any traceback): reframe_acp.compute_reframe( 0, cr )</li>
</ul>`
  ),

  "2-6-3-use": C(
    "2.6.3 Use",
    `<p>Select the required transformation in the options 'Transform' tab:</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/SEPM-Xtranslator-options-TransformTab.png" alt="SEPM X-Translator Options - Transform tab with LV03/LV95 and other coordinate transformations" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>X-Translator Options - Transform tab with Special Transform (LV03/LV95, RGF93, etc.)</figcaption>
</figure>
<p>If the database is set up in LV03, select the following:</p>
<ul>
<li>LV03/CH1903 -&gt; LV95/CH1903+ Export from LV03 to LV95</li>
<li>LV95/CH1903+ -&gt; LV03/CH1903 Import from LV95 to LV03</li>
</ul>
<p>If the database is set up in another coordinate system, please turn to support@sepm.ch for clarification.</p>`
  ),

  "2-7-1-prerequisites": C(
    "2.7.1 Prerequisites (SEPM 2025-01 and newer)",
    `<p>DWGAcp 150 (RealDWG 2025, SEPM 2025-01) no longer uses .NET Framework; it runs on .NET 8. Install the following before running DwgAcpSetup.msi:</p>
<ul>
<li>.NET 8 Runtime (x64) - the regular .NET Runtime is sufficient. (The SDK also works but is not required.)</li>
<li>Microsoft Visual C++ 2015–2022 Redistributable (x64) - required by RealDWG native components. Please consult the page "Microsoft Visual C++ Redistributable latest supported downloads" and download and install the version for Visual Studio 2022.</li>
</ul>`
  ),

  "2-7-2-installation": C(
    "2.7.2 Installation",
    `<p>Please follow the following steps for installation:</p>
<ul>
<li>Execute the Setup in setups\\dwgacp\\DwgAcpSetup.msi using the default path suggested by the installer (e.g. in Release 2025-01 it is C:\\Program Files\\SEPM\\DwgAcp150)</li>
<li>The directory configured in x_base_settings.dwgacp_path is added to the SW_ACP_PATH the first time the DwgAcp is called, if this directory exists. See dwgacp.fixup_acp_path() and x_base_settings.fixup_acp_path(). In most cases no further installations are necessary.</li>
<li>The installation must be performed on each client where the AutoCAD DWG/DXF format will be used.</li>
</ul>
<p>If you want to install DwgAcpSetup.msi into another directory, you'll need to add the directory selected in the setup to the SW_ACP_PATH. The easiest way to achieve this is through the gis_aliases file: SW_ACP_PATH = %SW_ACP_PATH%;C:\\DwgAcp150</p>
<p>The installation must be performed on each client where the AutoCAD DWG/DXF format will be used.</p>
<figure class="doc-image">
  <img src="/images/x-translator-sw/The%20installation%20must%20be%20performed%20on%20each%20client%20where%20the%20AutoCAD%20DWG-DXF%20format%20will%20be%20used.png" alt="DwgAcp150 - Select Installation Folder" class="max-w-full rounded-lg border border-gray-200 dark:border-gray-600" />
  <figcaption>DwgAcp installation - Select Installation Folder (default: C:\Program Files\SEPM\DwgAcp150)</figcaption>
</figure>
<p>The installation can be performed with the following command without GUI: msiexec /quiet /i &lt;lokales X-Translator Verzeichnis&gt;\\setups\\dwgacp\\DwgAcpSetup.msi</p>`
  ),

  "2-7-3-note-install": C(
    "2.7.3 Note on installation from version 2025-01",
    `<p>Starting with version 2025-01, DwgAcp no longer uses the .NET Framework, but rather .NET 8. This makes it possible to install DwgAcp version 2025-01 or newer in parallel with older versions (e.g., 2023-02), as there are no version conflicts with the .NET Framework:</p>
<ul>
<li>To run the new DwgAcp (2025-01 or newer), you must install the .NET 8 Runtime and the Microsoft Visual C++ Redistributables for Visual Studio 2022.</li>
<li>Older versions (DwgAcp 2024-02 or earlier) are based on the classic .NET Framework (version 4.8).</li>
</ul>
<p>In general, however, only one version of DwgAcp should be installed on a computer, corresponding to the SEPM version currently in use.</p>`
  ),

  "2-7-4-verification": C(
    "2.7.4 Verification / Troubleshooting",
    `<p>Steps to troubleshoot:</p>
<ul>
<li>Check .NET: dotnet –info. Check Microsoft.NETCore.App 8.0.x present. If dotnet is not recognized, re-open the shell or re-logon after install.</li>
<li>Check VC++: Apps &amp; Features shows Microsoft Visual C++ 2015–2022 Redistributable (x64).</li>
<li>Version mismatch: If you see "DwgAcp protocol version does not match", uninstall the old DWGAcp and install the one bundled with your SEPM version.</li>
</ul>`
  ),

  "2-7-5-upgrade": C(
    "2.7.5 Upgrade",
    `<p>When upgrading to a new X-Translator version, the existing DwgAcpSetup.msi usually has to be uninstalled and the version bundled with the new SEPM release installed. The Magik version of the ACP (dwgacp.version) and the installed DwgAcpSetup.msi must always match. If this is not the case, the message 'The DwgAcp protocol version does not match (Magik: #1, ACP: #2)' is displayed.</p>`
  ),
};
